import { useSearchParams, useLocation, Link, useNavigate } from "react-router-dom"
import { Copy, ArrowLeft, RotateCcw, Linkedin } from "lucide-react"
import { useState } from "react"
import { Button } from "@/modules/shared/ui/components/button"
import { Textarea } from "@/modules/shared/ui/components/textarea"
import { Switch } from "@/modules/shared/ui/components/switch"
import { Label } from "@/modules/shared/ui/components/label"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/modules/shared/ui/components/tooltip"
import { LINKEDIN_FEED_CHAR_LIMIT } from "@/modules/recruitment/vacancies/lib/build-linkedin-share-text"

const LINKEDIN_READABILITY_SOFT_LIMIT = 2000

interface LinkedInShareState {
  raw?: string
  trimmed?: string
  applyLink?: string
}

export default function LinkedInSharePage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const state = (location.state as LinkedInShareState | null) ?? {}

  const urlText = searchParams.get("text") ?? ""
  const rawText = state.raw ?? urlText
  const trimmedText = state.trimmed ?? rawText

  const [useTrimmed, setUseTrimmed] = useState(false)
  const [rawContent, setRawContent] = useState(rawText)
  const [trimmedContent, setTrimmedContent] = useState(trimmedText)
  const [copied, setCopied] = useState(false)

  const content = useTrimmed ? trimmedContent : rawContent
  const setContent = useTrimmed ? setTrimmedContent : setRawContent
  const currentPresetSource = useTrimmed ? trimmedText : rawText
  const isDirty = content !== currentPresetSource

  const overLimit = content.length > LINKEDIN_FEED_CHAR_LIMIT
  const overSoftLimit =
    !overLimit && content.length > LINKEDIN_READABILITY_SOFT_LIMIT

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(content)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleShareToLinkedIn = () => {
    const linkedinUrl = `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(content)}`
    window.open(linkedinUrl, "_blank")
  }

  const handleDiscardEdits = () => {
    if (useTrimmed) setTrimmedContent(trimmedText)
    else setRawContent(rawText)
  }

  const handleBack = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    navigate(-1)
  }

  return (
    <div className="p-4 space-y-6">
      <div className="bg-card rounded-lg border border-border p-8 shadow-sm">
        <Link
          to="#"
          onClick={handleBack}
          className="text-sm font-semibold inline-flex items-center gap-2 border border-transparent hover:text-primary text-muted-foreground transition-all duration-200 h-10 px-3 rounded-md"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Link>
        <div className="flex items-center justify-between mb-2 mt-2">
          <h1 className="text-3xl font-bold text-foreground">
            Share to LinkedIn
          </h1>
        </div>
        <p className="text-muted-foreground mb-6">
          Edit, copy, or post directly. LinkedIn accepts plain text only —
          formatting won't carry over.
        </p>

        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
            <h2 className="text-sm font-semibold text-foreground">
              Post content
            </h2>
            <TooltipProvider delayDuration={150}>
              <div className="flex items-center gap-3">
                <Label
                  htmlFor="trimmed-switch"
                  className="text-xs font-normal text-muted-foreground cursor-pointer flex items-center gap-2"
                >
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        className={
                          !useTrimmed ? "text-foreground font-medium" : ""
                        }
                      >
                        Raw
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[240px]">
                      <p className="text-[11px] leading-relaxed">
                        The full recommended post — every section included, no
                        trimming. May exceed LinkedIn's{" "}
                        {LINKEDIN_FEED_CHAR_LIMIT}-character limit; if it does,
                        LinkedIn will silently cut the end of your post
                        (including your apply link) when you paste it in.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                  <Switch
                    id="trimmed-switch"
                    checked={useTrimmed}
                    onCheckedChange={setUseTrimmed}
                  />
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        className={
                          useTrimmed ? "text-foreground font-medium" : ""
                        }
                      >
                        Trimmed to fit
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[240px]">
                      <p className="text-[11px] leading-relaxed">
                        Automatically shortened to fit LinkedIn's{" "}
                        {LINKEDIN_FEED_CHAR_LIMIT}-character limit — drops
                        optional sections in order (Nice to Have →
                        Qualifications → Required Skills → Responsibilities)
                        and then trims the description if still over. Your
                        apply link is always preserved.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </Label>
                {isDirty && (
                  <span className="text-xs text-muted-foreground italic">
                    edited
                  </span>
                )}
              </div>
            </TooltipProvider>
          </div>

          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className="min-h-96 font-mono text-sm leading-relaxed whitespace-pre-wrap resize-y"
            spellCheck
          />

          <div className="mt-4 text-sm text-muted-foreground">
            Character count:{" "}
            <span className="font-semibold">{content.length}</span>
            <span className="text-muted-foreground">
              {" "}/ {LINKEDIN_FEED_CHAR_LIMIT}
            </span>
            {overLimit && (
              <span className="ml-2 text-red-600 font-medium">
                Over LinkedIn's {LINKEDIN_FEED_CHAR_LIMIT}-character limit —
                LinkedIn will silently truncate from the end, including your
                apply link.
              </span>
            )}
            {overSoftLimit && (
              <span className="ml-2 text-amber-600">
                Aim for under {LINKEDIN_READABILITY_SOFT_LIMIT} characters for
                best readability.
              </span>
            )}
          </div>

          <div className="flex gap-3 mt-6 flex-wrap">
            <Button
              onClick={handleDiscardEdits}
              variant="outline"
              disabled={!isDirty}
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Discard edits
            </Button>
            <Button
              onClick={handleCopyToClipboard}
              variant="outline"
              className="flex-1 min-w-40"
            >
              <Copy className="mr-2 h-4 w-4" />
              {copied ? "Copied!" : "Copy to Clipboard"}
            </Button>
            <Button
              onClick={handleShareToLinkedIn}
              className="flex-1 min-w-40"
            >
              <Linkedin className="mr-2 h-4 w-4" />
              Share to LinkedIn
            </Button>
          </div>
      </div>
    </div>
  )
}

